import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, BatchProcessResult, CreateOfficialStyleInput } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useToast } from "@/components/ui/Toast";
import {
  Loader2,
  Check,
  X,
  AlertCircle,
  Play,
  FolderUp,
  Sparkles,
  Edit,
  Wand2,
  RotateCcw,
} from "lucide-react";

// Upload guardrails surfaced to the user before processing.
const MAX_IMAGES = 500;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
// Stop polling after this many consecutive failures or this long overall.
const MAX_POLL_FAILURES = 5;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 minutes

type Step = "upload" | "configure" | "processing" | "review";

interface UploadedImage {
  file: File;
  preview: string;
  url?: string;
}

interface ProcessedStyle extends BatchProcessResult {
  selected: boolean;
  edited: boolean;
}

const categories = [
  "自動偵測",
  "簡報",
  "插畫",
  "海報",
  "社群媒體",
  "圖表",
  "概念圖",
  "UI 設計",
  "其他",
];

export default function BatchCreatePage() {
  const [step, setStep] = useState<Step>("upload");
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [config, setConfig] = useState({
    defaultCategory: "自動偵測",
    autoGenerateTriggerScene: true,
    autoGeneratePrompt: true,
    autoGenerateTags: true,
    autoGenerateShowcaseImage: false,
  });
  const [currentPhase, setCurrentPhase] = useState<"analyzing" | "generating" | "done">("analyzing");

  // Check if AI generation is available
  const { data: generateStatus } = useQuery({
    queryKey: ["generate-status"],
    queryFn: api.getGenerateStatus,
  });
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<ProcessedStyle[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // Error surfaced on the processing screen so the user isn't stuck on an
  // infinite spinner when polling fails or times out.
  const [processingError, setProcessingError] = useState<string | null>(null);
  // Flips to true to stop an in-flight poll loop (cancel button).
  const cancelledRef = useRef(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const response = await api.uploadImages(files);
      return response.urls;
    },
  });

  const startBatchMutation = useMutation({
    mutationFn: api.startBatchProcess,
  });

  const confirmMutation = useMutation({
    mutationFn: ({ jobId, styles }: { jobId: string; styles: CreateOfficialStyleInput[] }) =>
      api.confirmBatchResults(jobId, styles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["styles"] });
    },
  });

  const addImages = useCallback((files: File[]) => {
    // Reject oversized files up front rather than failing mid-processing.
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    const sized = files.filter((f) => f.size <= MAX_FILE_SIZE);
    if (oversized.length > 0) {
      toast.warning(`已略過 ${oversized.length} 個超過 10MB 的檔案`);
    }

    setImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      if (remaining <= 0) {
        toast.warning(`最多只能上傳 ${MAX_IMAGES} 張圖片`);
        return prev;
      }
      const accepted = sized.slice(0, remaining);
      if (sized.length > remaining) {
        toast.warning(`已達 ${MAX_IMAGES} 張上限，超出的 ${sized.length - remaining} 張未加入`);
      }
      const newImages = accepted.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      return [...prev, ...newImages];
    });
  }, [toast]);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );
    addImages(files);
  }, [addImages]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    addImages(files);
  };

  const removeImage = (index: number) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleStartProcess = async () => {
    // First upload all images
    cancelledRef.current = false;
    setProcessingError(null);
    setStep("processing");
    setProgress({ current: 0, total: images.length });

    try {
      const urls = await uploadMutation.mutateAsync(images.map((img) => img.file));

      // Update images with URLs
      const updatedImages = images.map((img, i) => ({
        ...img,
        url: urls[i],
      }));
      setImages(updatedImages);

      // Start batch processing
      const { jobId } = await startBatchMutation.mutateAsync({
        imageUrls: urls,
        defaultCategory: config.defaultCategory === "自動偵測" ? undefined : config.defaultCategory,
        autoGenerateTriggerScene: config.autoGenerateTriggerScene,
        autoGeneratePrompt: config.autoGeneratePrompt,
        autoGenerateTags: config.autoGenerateTags,
        autoGenerateShowcaseImage: config.autoGenerateShowcaseImage,
      });

      setJobId(jobId);

      // Poll for progress
      await pollJobStatus(jobId);
    } catch (error) {
      console.error("Batch process failed:", error);
      setProcessingError(error instanceof Error ? error.message : "批量處理失敗，請重試");
    }
  };

  /**
   * Poll job status with bounded retries and an overall timeout. Errors and
   * timeouts surface an inline, retryable error state instead of leaving the
   * user stuck on a perpetual spinner.
   */
  const pollJobStatus = async (jobId: string) => {
    const startedAt = Date.now();
    let failures = 0;

    const poll = async (): Promise<void> => {
      if (cancelledRef.current) return;

      if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
        setProcessingError("處理逾時，請稍後重試或檢查伺服器狀態");
        return;
      }

      try {
        const job = await api.getBatchJobStatus(jobId);
        failures = 0;
        setProgress({ current: job.processedImages, total: job.totalImages });

        // Update current phase for better UX
        if (job.currentPhase) {
          setCurrentPhase(job.currentPhase);
        }

        if (job.status === "completed") {
          setResults(
            job.results.map((r) => ({
              ...r,
              selected: r.status === "success",
              edited: false,
            }))
          );
          setStep("review");
        } else if (job.status === "failed") {
          setProcessingError("伺服器回報批量處理失敗，請重試");
        } else if (!cancelledRef.current) {
          setTimeout(poll, 2000);
        }
      } catch (error) {
        failures += 1;
        console.error("Poll failed:", error);
        if (failures >= MAX_POLL_FAILURES) {
          setProcessingError("無法取得處理進度，請檢查網路後重試");
        } else if (!cancelledRef.current) {
          // Back off briefly, then retry.
          setTimeout(poll, 3000);
        }
      }
    };

    await poll();
  };

  const handleCancelProcessing = () => {
    cancelledRef.current = true;
    setProcessingError(null);
    setStep("configure");
  };

  const handleRetryProcessing = () => {
    setProcessingError(null);
    if (jobId) {
      // Resume polling an already-started job.
      cancelledRef.current = false;
      setStep("processing");
      void pollJobStatus(jobId);
    } else {
      void handleStartProcess();
    }
  };

  const handleToggleSelect = (index: number) => {
    setResults((prev) =>
      prev.map((r, i) => (i === index ? { ...r, selected: !r.selected } : r))
    );
  };

  const handleUpdateResult = (index: number, updates: Partial<BatchProcessResult>) => {
    setResults((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, ...updates, edited: true } : r
      )
    );
    setEditingIndex(null);
  };

  const handleConfirm = async () => {
    if (!jobId) return;

    const selectedStyles = results
      .filter((r) => r.selected && r.status === "success")
      .map((r) => ({
        name: r.name,
        triggerScene: r.triggerScene,
        prompt: r.prompt,
        category: r.category,
        tags: r.tags,
        // Use generated showcase image if available, otherwise use original uploaded image
        referenceImages: r.generatedImageUrl ? [r.generatedImageUrl] : [r.imageUrl],
        status: "DRAFT" as const,
      }));

    try {
      const { created } = await confirmMutation.mutateAsync({
        jobId,
        styles: selectedStyles,
      });
      toast.success(`成功建立 ${created} 個模板`, {
        action: { label: "前往風格列表", onClick: () => navigate("/styles") },
      });
      // Reset state
      setStep("upload");
      setImages([]);
      setResults([]);
      setJobId(null);
    } catch (error) {
      console.error("Confirm failed:", error);
      toast.error(error instanceof Error ? error.message : "確認失敗，請重試");
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-ceramic-900">批量製作模板</h1>
        <p className="text-ceramic-500 mt-1">
          上傳多張圖片，AI 會自動分析並生成對應的模板提示詞
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-4 mb-8">
        {[
          { key: "upload", label: "上傳圖片" },
          { key: "configure", label: "設定參數" },
          { key: "processing", label: "處理中" },
          { key: "review", label: "審核確認" },
        ].map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                step === s.key
                  ? "bg-accent text-white"
                  : ["upload", "configure", "processing", "review"].indexOf(step) >
                    ["upload", "configure", "processing", "review"].indexOf(s.key)
                  ? "bg-green-500 text-white"
                  : "bg-ceramic-200 text-ceramic-600"
              )}
            >
              {["upload", "configure", "processing", "review"].indexOf(step) >
              ["upload", "configure", "processing", "review"].indexOf(s.key) ? (
                <Check className="w-4 h-4" />
              ) : (
                i + 1
              )}
            </div>
            <span
              className={cn(
                "text-sm",
                step === s.key ? "text-ceramic-900 font-medium" : "text-ceramic-500"
              )}
            >
              {s.label}
            </span>
            {i < 3 && <div className="w-12 h-px bg-ceramic-200 mx-2" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      {step === "upload" && (
        <div className="space-y-6">
          {/* Drop Zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-ceramic-300 rounded-xl p-12 text-center cursor-pointer hover:border-accent hover:bg-accent/5 transition-colors"
          >
            <FolderUp className="w-12 h-12 text-ceramic-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-ceramic-700">
              拖曳資料夾或圖片至此處
            </p>
            <p className="text-sm text-ceramic-500 mt-2">
              支援 PNG, JPG, WebP (最多 500 張)
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Preview Grid */}
          {images.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-ceramic-900">
                  已選擇 {images.length} 張圖片
                </h3>
                <button
                  onClick={() => {
                    images.forEach((img) => URL.revokeObjectURL(img.preview));
                    setImages([]);
                  }}
                  className="text-sm text-red-500 hover:text-red-600"
                >
                  清除全部
                </button>
              </div>
              <div className="grid grid-cols-6 gap-4">
                {images.map((img, i) => (
                  <div key={i} className="relative group aspect-square">
                    <img
                      src={img.preview}
                      alt={`Preview ${i + 1}`}
                      className="w-full h-full object-cover rounded-lg border border-ceramic-200"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(i);
                      }}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Button */}
          <div className="flex justify-end">
            <button
              onClick={() => setStep("configure")}
              disabled={images.length === 0}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一步
            </button>
          </div>
        </div>
      )}

      {step === "configure" && (
        <div className="space-y-6">
          <div className="bg-white border border-ceramic-200 rounded-xl p-6">
            <h3 className="text-lg font-medium text-ceramic-900 mb-4">處理設定</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ceramic-700 mb-2">
                  預設分類
                </label>
                <select
                  value={config.defaultCategory}
                  onChange={(e) =>
                    setConfig((prev) => ({ ...prev, defaultCategory: e.target.value }))
                  }
                  className="w-full px-4 py-3 border border-ceramic-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.autoGenerateTriggerScene}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        autoGenerateTriggerScene: e.target.checked,
                      }))
                    }
                    className="w-5 h-5 rounded border-ceramic-300 text-accent focus:ring-accent"
                  />
                  <span className="text-ceramic-700">自動生成觸發情景</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.autoGeneratePrompt}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        autoGeneratePrompt: e.target.checked,
                      }))
                    }
                    className="w-5 h-5 rounded border-ceramic-300 text-accent focus:ring-accent"
                  />
                  <span className="text-ceramic-700">自動生成提示詞</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.autoGenerateTags}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        autoGenerateTags: e.target.checked,
                      }))
                    }
                    className="w-5 h-5 rounded border-ceramic-300 text-accent focus:ring-accent"
                  />
                  <span className="text-ceramic-700">自動標記標籤</span>
                </label>

                {/* Auto Generate Showcase Image */}
                {generateStatus?.available && (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.autoGenerateShowcaseImage}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          autoGenerateShowcaseImage: e.target.checked,
                        }))
                      }
                      className="w-5 h-5 rounded border-ceramic-300 text-accent focus:ring-accent"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-ceramic-700">自動生成展示圖片</span>
                      <Wand2 className="w-4 h-4 text-accent" />
                    </div>
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">AI 分析說明</p>
              <p className="text-sm text-blue-700 mt-1">
                AI 將分析每張圖片的視覺特徵，包含色彩調性、排版結構、設計模板等，
                並自動生成對應的提示詞。處理時間約為每張圖片 3-5 秒。
                {config.autoGenerateShowcaseImage && (
                  <span className="block mt-1 font-medium">
                    啟用「自動生成展示圖片」後，系統會根據分析出的模板提示詞，使用 AI 生成對應的展示插圖。
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep("upload")}
              className="px-6 py-3 border border-ceramic-200 rounded-lg hover:bg-ceramic-50 transition-colors"
            >
              上一步
            </button>
            <button
              onClick={handleStartProcess}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
            >
              <Play className="w-4 h-4" />
              開始處理 ({images.length} 張)
            </button>
          </div>
        </div>
      )}

      {step === "processing" && (
        <div className="flex flex-col items-center justify-center py-20">
          {processingError ? (
            <>
              <div className="mb-6 rounded-full bg-red-50 p-4">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-xl font-medium text-ceramic-900 mb-2">處理發生問題</h3>
              <p className="text-ceramic-500 mb-6 text-center max-w-md">{processingError}</p>
              <div className="flex gap-3">
                <button
                  onClick={handleCancelProcessing}
                  className="px-6 py-3 border border-ceramic-200 rounded-lg hover:bg-ceramic-50 transition-colors"
                >
                  返回設定
                </button>
                <button
                  onClick={handleRetryProcessing}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  重試
                </button>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="w-12 h-12 text-accent animate-spin mb-6" />
              <h3 className="text-xl font-medium text-ceramic-900 mb-2">
                {currentPhase === "analyzing" && "AI 正在分析圖片..."}
                {currentPhase === "generating" && "AI 正在生成展示圖片..."}
                {currentPhase === "done" && "處理完成！"}
              </h3>
              <p className="text-ceramic-500 mb-6">
                {currentPhase === "analyzing" && (
                  <>已分析 {progress.current} / {progress.total} 張</>
                )}
                {currentPhase === "generating" && (
                  <>已生成 {progress.current} / {progress.total} 張展示圖片</>
                )}
                {currentPhase === "done" && "正在準備結果..."}
              </p>
              <div className="w-80 h-2 bg-ceramic-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all duration-300"
                  style={{
                    width: `${
                      progress.total > 0 ? (progress.current / progress.total) * 100 : 0
                    }%`,
                  }}
                />
              </div>
              {currentPhase === "generating" && (
                <p className="text-xs text-ceramic-400 mt-4">
                  使用 Cloudflare AI FLUX 生成展示圖片
                </p>
              )}
              <button
                onClick={handleCancelProcessing}
                className="mt-8 px-6 py-2 text-sm border border-ceramic-200 rounded-lg text-ceramic-600 hover:bg-ceramic-50 transition-colors"
              >
                取消處理
              </button>
            </>
          )}
        </div>
      )}

      {step === "review" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-ceramic-900">
                處理完成！請確認結果
              </h3>
              <p className="text-sm text-ceramic-500 mt-1">
                成功 {results.filter((r) => r.status === "success").length} 張，
                失敗 {results.filter((r) => r.status === "failed").length} 張
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-ceramic-600">
                已選 {results.filter((r) => r.selected).length} 項
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {results.map((result, index) => (
              <div
                key={index}
                className={cn(
                  "bg-white border rounded-xl p-4 transition-colors",
                  result.status === "failed"
                    ? "border-red-200 bg-red-50"
                    : result.selected
                    ? "border-accent"
                    : "border-ceramic-200"
                )}
              >
                <div className="flex gap-4">
                  {/* Checkbox & Image */}
                  <div className="flex items-start gap-3">
                    {result.status === "success" && (
                      <input
                        type="checkbox"
                        checked={result.selected}
                        onChange={() => handleToggleSelect(index)}
                        className="mt-1 w-5 h-5 rounded border-ceramic-300 text-accent focus:ring-accent"
                      />
                    )}
                    <div className="flex gap-2">
                      {/* Original uploaded image */}
                      <div className="relative">
                        <img
                          src={result.imageUrl}
                          alt={result.name}
                          className="w-24 h-24 object-cover rounded-lg border border-ceramic-200"
                        />
                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/50 text-white text-xs rounded">
                          原圖
                        </span>
                      </div>
                      {/* Generated showcase image (if available) */}
                      {result.generatedImageUrl && (
                        <div className="relative">
                          <img
                            src={result.generatedImageUrl}
                            alt={`${result.name} - 展示圖`}
                            className="w-24 h-24 object-cover rounded-lg border-2 border-accent"
                          />
                          <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-accent text-white text-xs rounded flex items-center gap-1">
                            <Wand2 className="w-3 h-3" />
                            AI
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {result.status === "failed" ? (
                      <div className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        <span>處理失敗：{result.error}</span>
                      </div>
                    ) : editingIndex === index ? (
                      <EditForm
                        result={result}
                        onSave={(updates) => handleUpdateResult(index, updates)}
                        onCancel={() => setEditingIndex(null)}
                      />
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-ceramic-900">
                            {result.name}
                          </h4>
                          <span className="px-2 py-0.5 bg-ceramic-100 text-ceramic-600 text-xs rounded">
                            {result.category}
                          </span>
                          {result.edited && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">
                              已編輯
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-ceramic-600 line-clamp-2 mb-2">
                          {result.prompt}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {result.tags.slice(0, 8).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-ceramic-100 text-ceramic-600 text-xs rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {result.tags.length > 8 && (
                            <span className="px-2 py-0.5 bg-ceramic-100 text-ceramic-500 text-xs rounded">
                              +{result.tags.length - 8}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  {result.status === "success" && editingIndex !== index && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingIndex(index)}
                        className="p-2 text-ceramic-500 hover:text-ceramic-700 hover:bg-ceramic-100 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => {
                setStep("upload");
                setResults([]);
                setJobId(null);
              }}
              className="px-6 py-3 border border-ceramic-200 rounded-lg hover:bg-ceramic-50 transition-colors"
            >
              重新開始
            </button>
            <button
              onClick={handleConfirm}
              disabled={
                results.filter((r) => r.selected).length === 0 ||
                confirmMutation.isPending
              }
              className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirmMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              確認建立 ({results.filter((r) => r.selected).length} 個模板)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Edit Form Component
function EditForm({
  result,
  onSave,
  onCancel,
}: {
  result: BatchProcessResult;
  onSave: (updates: Partial<BatchProcessResult>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(result.name);
  const [prompt, setPrompt] = useState(result.prompt);
  const [triggerScene, setTriggerScene] = useState(result.triggerScene);
  const category = result.category;

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full px-3 py-2 border border-ceramic-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        placeholder="模板名稱"
      />
      <textarea
        value={triggerScene}
        onChange={(e) => setTriggerScene(e.target.value)}
        className="w-full px-3 py-2 border border-ceramic-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-none"
        rows={2}
        placeholder="觸發情景"
      />
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        className="w-full px-3 py-2 border border-ceramic-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent resize-none"
        rows={3}
        placeholder="提示詞"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSave({ name, prompt, triggerScene, category })}
          className="px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-hover transition-colors"
        >
          儲存
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 border border-ceramic-200 text-sm rounded-lg hover:bg-ceramic-50 transition-colors"
        >
          取消
        </button>
      </div>
    </div>
  );
}
